import { SimpleGrid } from "@mantine/core";
import { useMemo } from "react";

import { useGetVisibility } from "../../../api/insights";
import { RankedBarChart } from "../charts";
import SectionShell, { usePartShown } from "../SectionShell";
import { useChartTimeFrame } from "../useChartTimeFrame";
import { toRangeParams } from "../timeFrame";

const toChart = (rows) =>
  rows.map((r) => ({
    name: r.name ?? "(unknown)",
    Views: r.viewCount,
    "Unique users": r.uniqueUsers,
    "Link clicks": r.linkClicks ?? 0,
  }));

const TopBars = ({ title, rows }) => {
  const hasLinkClicks = rows.some((r) => r["Link clicks"] > 0);
  return (
    <RankedBarChart
      title={title}
      data={rows}
      categoryKey="name"
      yAxisWidth={104}
      series={[
        ...(hasLinkClicks
          ? [{ name: "Link clicks", color: "violet.3", stackId: "totals" }]
          : []),
        { name: "Unique users", color: "teal.6", stackId: "totals" },
        { name: "Views", color: "blue.6", stackId: "totals" },
      ]}
    />
  );
};

function ProfileViews({ eventId }) {
  const tf = useChartTimeFrame(eventId);
  const speakersPartShown = usePartShown("topSpeakers");
  const sponsorsPartShown = usePartShown("topSponsors");
  const { data, isPending, isError, error } = useGetVisibility(
    eventId,
    toRangeParams(tf.range)
  );
  const speakers = useMemo(
    () => toChart(data?.mostViewedSpeakers ?? []),
    [data]
  );
  const sponsors = useMemo(
    () => toChart(data?.mostViewedSponsors ?? []),
    [data]
  );

  // Counts default to "present" when missing so the section isn't hidden
  // before the data (or an older API response) settles.
  const hasSpeakers = data?.totalSpeakers == null || data.totalSpeakers > 0;
  const hasSponsors = data?.totalSponsors == null || data.totalSponsors > 0;

  if (!isPending && !isError && !hasSpeakers && !hasSponsors) return null;

  const showSpeakers = hasSpeakers && speakersPartShown;
  const showSponsors = hasSponsors && sponsorsPartShown;

  const title =
    hasSpeakers && hasSponsors
      ? "Speaker & sponsor visibility"
      : hasSpeakers
        ? "Speaker visibility"
        : "Sponsor visibility";
  const description =
    hasSpeakers && hasSponsors
      ? "Most viewed speaker and sponsor profiles."
      : hasSpeakers
        ? "Most viewed speaker profiles."
        : "Most viewed sponsor profiles.";

  return (
    <SectionShell
      id="profileViews"
      title={title}
      description={description}
      isPending={isPending}
      isError={isError}
      error={error}
      eventId={eventId}
      analysisKey="profileViews"
      headerRight={tf.control}
    >
      <SimpleGrid
        cols={{ base: 1, md: showSpeakers && showSponsors ? 2 : 1 }}
        spacing="md"
      >
        {showSpeakers && <TopBars title="Top speakers" rows={speakers} />}
        {showSponsors && <TopBars title="Top sponsors" rows={sponsors} />}
      </SimpleGrid>
    </SectionShell>
  );
}

export default ProfileViews;
